import { Injectable } from "@angular/core";
import {
	MatLegacySnackBarDismiss as MatSnackBarDismiss,
	MatLegacySnackBarRef as MatSnackBarRef,
} from "@angular/material/legacy-snack-bar";
import { select, Store } from "@ngrx/store";
import { SnackBarComponentBase } from "@obg/mat.ui.core/src/snackbar/snackbar.model";
import {
	CommonState,
	cookieConsentAccepted,
	CookieConsentAction,
	CookieConsentSelector,
	CookieNoticeAction,
	CookieNoticeSelector,
} from "@obg/ngx.common";
import {
	AbstractTranslatableSnackBarService,
	DialogStatusSelector,
	MessageType,
	TrackingAreaService,
} from "@obg/ngx.core";
import { Route, RouteSelector } from "@obg/ngx.router";
import { combineLatest, merge, Observable } from "rxjs";
import { distinctUntilChanged, filter, map, scan, skipWhile, switchMap, take, tap } from "rxjs/operators";

import { CookieNoticePanelClasses } from "./cookie-notice.model";

@Injectable({
	providedIn: "root",
})
export class CookieNoticeService {
	private cookieSnackBar: MatSnackBarRef<SnackBarComponentBase> | undefined;

	constructor(
		private store: Store<CommonState>,
		private cookieNoticeSelector: CookieNoticeSelector,
		private cookieNoticeAction: CookieNoticeAction,
		private cookieConsentSelector: CookieConsentSelector,
		private cookieConsentAction: CookieConsentAction,
		private abstractTranslatableSnackBarService: AbstractTranslatableSnackBarService,
		private trackingAreaService: TrackingAreaService,
		private dialogStatusSelector: DialogStatusSelector
	) {}

	initCookieSnackbar(panelClasses?: CookieNoticePanelClasses): Observable<MatSnackBarDismiss | undefined> {
		const cookieSnackBar$ = combineLatest([
			this.store.pipe(select(this.cookieNoticeSelector.shouldShowCookieNotice())),
			this.store.select(RouteSelector.getCurrent),
			this.store.pipe(select(this.dialogStatusSelector.hasOpenDialogs())),
		]).pipe(
			map(([shouldShowCookieNotice, route, hasOpenDialogs]) => [
				false,
				!!shouldShowCookieNotice && !hasOpenDialogs && !!route && this.isRouteLobbyMainPage(route),
			])
		);

		const gdprCookieSnackBar$ = this.store.pipe(
			select(this.cookieConsentSelector.shouldAskConsent()),
			map((shouldShowCookieConsent) => [true, shouldShowCookieConsent])
		);

		return merge(cookieSnackBar$, gdprCookieSnackBar$).pipe(
			skipWhile(([, shouldShow]) => !shouldShow),
			scan(([isGdprCurrent, isShowingCurrent], [isGdprNext, isShowingNext]) => {
				// it should be everything GDPR or everything non-GDPR. Mixing of contexts shouldn't occur
				if (isGdprCurrent !== isGdprNext) {
					return [isGdprCurrent, isShowingCurrent];
				}

				return [isGdprNext, isShowingNext];
			}),
			distinctUntilChanged(([, isShowingCurrent], [, isShowingNext]) => isShowingCurrent === isShowingNext),
			tap(([, shouldShow]) => {
				if (!shouldShow && this.cookieSnackBar) {
					this.cookieSnackBar.dismiss();
					this.cookieSnackBar = undefined;
				}
			}),
			filter(([, shouldShow]) => shouldShow && !this.cookieSnackBar),
			switchMap(([isGdpr]) => this.showCookieSnackBar$(isGdpr, panelClasses))
		);
	}

	private showCookieSnackBar$(
		isGdprEnabled: boolean,
		panelClasses?: CookieNoticePanelClasses
	): Observable<MatSnackBarDismiss> {
		let classes;
		if (panelClasses) {
			classes = isGdprEnabled ? panelClasses.gdprNotice : panelClasses.cookieNotice;
		}

		return this.abstractTranslatableSnackBarService
			.openSnackBar$({
				message: isGdprEnabled ? "general.gdpr-notice" : "general.cookie-notice",
				type: MessageType.Warn,
				action: isGdprEnabled ? "general.accept" : "x",
				panelClass: classes,
				verticalPosition: "bottom",
				keepOpen: true,
			})
			.pipe(
				take(1),
				switchMap((snackBarRef) => {
					this.cookieSnackBar = snackBarRef;
					return snackBarRef.afterDismissed().pipe(
						tap((dismissInfo: MatSnackBarDismiss) => {
							this.cookieSnackBar = undefined;

							if (!isGdprEnabled) {
								this.store.dispatch(this.cookieNoticeAction.close());
								return;
							}

							if (dismissInfo.dismissedByAction) {
								this.store.dispatch(this.cookieConsentAction.update(cookieConsentAccepted));
							}
						})
					);
				})
			);
	}

	private isRouteLobbyMainPage(route: Route): boolean {
		const tracking = this.trackingAreaService.getTrackingData(route);
		return !!tracking?.lobby;
	}
}
